import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";

import {
  Alert,
  Button,
  Card,
  CasePhaseStrip,
  Cluster,
  Input,
  PageHeading,
  Stack,
  TaskTable,
  useCaseTaskIssue,
} from "~/components";
import {
  caseProject,
  caseQueryOptions,
  caseTasksQueryOptions,
  formatValidationError,
  isValidationError,
  useDeleteCase,
  useUpdateCaseMutation,
  type Case,
} from "~/lib/cases";
import { contactName } from "~/lib/contacts";
import { formatPhone } from "~/lib/phones";
import {
  annotateEventsWithVerbs,
  caseEventsQueryOptions,
  taskLabelFromCondition,
  type CaseEvent,
} from "~/lib/events";
import { HalError } from "~/lib/hal";
import { queryClient } from "~/lib/queryClient";
import { workflowQueryOptions } from "~/lib/workflows";

import * as css from "~/routes/_app.cases.$id.css";

export const Route = createFileRoute("/_app/cases/$id")({
  component: EditCasePage,
  loader: async ({ params }) => {
    // Load the case first — its project id scopes the tasks + events
    // fetches (those resources are project-scoped now). Tasks + events
    // are below-the-fold but prefetched so the page paints in one shot.
    const caseRow = await queryClient.ensureQueryData(
      caseQueryOptions(params.id),
    );
    const project = caseProject(caseRow);
    if (project === undefined) return;
    await Promise.all([
      queryClient.ensureQueryData(caseTasksQueryOptions(params.id, project.id)),
      queryClient.ensureQueryData(caseEventsQueryOptions(params.id, project.id)),
    ]);
  },
});

function EditCasePage() {
  const { id } = Route.useParams();
  const { data, isPending, isError, error } = useQuery(caseQueryOptions(id));

  if (isPending) {
    return (
      <Stack gap="lg">
        <PageHeading title="Loading case…" />
        <Card>
          <p>Loading case…</p>
        </Card>
      </Stack>
    );
  }

  if (isError) {
    return <EditCaseError error={error} />;
  }

  return <EditCaseForm caseRow={data} />;
}

function EditCaseError({ error }: { error: Error }) {
  const router = useRouter();
  const isNotFound = error instanceof HalError && error.status === 404;
  return (
    <Stack gap="lg">
      <PageHeading title={isNotFound ? "Case not found" : "Couldn't load case"} />
      <Card accent="danger">
        <Stack gap="md">
          <p>{error.message}</p>
          <Cluster gap="sm">
            <Button
              type="button"
              variant="secondary"
              onClick={() => router.history.back()}
            >
              Back
            </Button>
            <Link to="/cases">
              <Button type="button" variant="secondary">
                Back to cases
              </Button>
            </Link>
          </Cluster>
        </Stack>
      </Card>
    </Stack>
  );
}

function EditCaseForm({ caseRow }: { caseRow: Case }) {
  const navigate = useNavigate();
  const updateMutation = useUpdateCaseMutation();

  // The workflow machinery (name, workflow link, plan) lives on the
  // case's project now (TMF-170).
  const project = caseProject(caseRow);

  // The workflow link is informational only. Fetch the workflow so we
  // can show its name next to its ID — keeps the user oriented without
  // forcing a round-trip back to the list just to confirm the link.
  const workflowQuery = useQuery({
    ...workflowQueryOptions(project?.workflow_id ?? ""),
    enabled: project !== undefined,
    // Don't block rendering on this — the form still works if it fails.
    retry: false,
  });

  const [filevineProjectId, setFilevineProjectId] = useState(
    caseRow.filevine_project_id !== null
      ? String(caseRow.filevine_project_id)
      : "",
  );

  const filevineProjectIdTrimmed = filevineProjectId.trim();
  const parsedFilevineProjectId =
    filevineProjectIdTrimmed === ""
      ? undefined
      : /^\d+$/.test(filevineProjectIdTrimmed)
        ? Number.parseInt(filevineProjectIdTrimmed, 10)
        : NaN;
  const filevineProjectIdInvalid =
    filevineProjectIdTrimmed !== "" &&
    (Number.isNaN(parsedFilevineProjectId) || parsedFilevineProjectId === 0);
  // Three states map to three save behaviors:
  //   empty + row had a value → send 0 (unlink; TMF-376 — `null` now reads as
  //     omitted server-side, so it would silently leave the link in place).
  //   valid positive int ≠ row value → send the new int.
  //   anything else (unchanged, invalid) → don't send the field.
  const filevineProjectIdCleared =
    filevineProjectIdTrimmed === "" && caseRow.filevine_project_id !== null;
  const filevineProjectIdSet =
    parsedFilevineProjectId !== undefined &&
    !Number.isNaN(parsedFilevineProjectId) &&
    parsedFilevineProjectId !== caseRow.filevine_project_id;
  const filevineProjectIdChanged =
    filevineProjectIdCleared || filevineProjectIdSet;

  const canSubmit = !filevineProjectIdInvalid && filevineProjectIdChanged;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    updateMutation.mutate(
      {
        id: caseRow.id,
        ...(filevineProjectIdCleared
          ? { filevine_project_id: 0 }
          : filevineProjectIdSet
            ? { filevine_project_id: parsedFilevineProjectId as number }
            : {}),
      },
      {
        onSuccess: () => {
          void navigate({ to: "/cases" });
        },
      },
    );
  };

  const mutationError = updateMutation.error;
  const mutationErrorIsValidation = isValidationError(mutationError);

  return (
    <Stack gap="lg">
      <PageHeading
        title={caseTitle(caseRow)}
        subtitle="The matter as the funnel captured it. The workflow, its plan and the person's own record are edited where they live."
        actions={
          <Link to="/cases">
            <Button type="button" variant="secondary">
              Back to cases
            </Button>
          </Link>
        }
      />
      {workflowQuery.data !== undefined && project !== undefined && (
        <Card>
          <CasePhaseStrip
            workflow={workflowQuery.data}
            plan={project.plan}
          />
        </Card>
      )}

      <IntakePanel caseRow={caseRow} />

      {project !== undefined ? (
        <>
          <TasksPanel caseId={caseRow.id} projectId={project.id} />

          <HistoryFeed caseId={caseRow.id} projectId={project.id} />
        </>
      ) : null}

      <Card>
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Stack gap="xs">
              <label>
                <strong>Workflow</strong>
              </label>
              <div>
                {workflowQuery.data !== undefined && project !== undefined ? (
                  <Link
                    to="/workflows/$id/edit"
                    params={{ id: project.workflow_id }}
                  >
                    {workflowQuery.data.name}
                  </Link>
                ) : (
                  <code>{project?.workflow_id}</code>
                )}
              </div>
              <small>
                The workflow assignment can't be changed after a case is
                created — changing it would orphan the case's plan.
              </small>
            </Stack>

            <Stack gap="xs">
              <label htmlFor="case-filevine-project-id">
                <strong>Filevine project ID</strong>{" "}
                <small>(optional)</small>
              </label>
              <Input
                id="case-filevine-project-id"
                inputMode="numeric"
                value={filevineProjectId}
                onChange={(e) => setFilevineProjectId(e.target.value)}
                placeholder="e.g., 12345678"
                aria-invalid={filevineProjectIdInvalid || undefined}
              />
              <small>
                Links the case to its Filevine project so automated
                agents can read documents and post notes. Clear the
                field to unlink.
              </small>
              {filevineProjectIdInvalid && (
                <small>Must be a positive integer.</small>
              )}
            </Stack>

            <div>
              <Button
                type="submit"
                variant="primary"
                disabled={updateMutation.isPending || !canSubmit}
              >
                {updateMutation.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>

            {mutationError !== null && (
              <Alert variant="danger" icon={<AlertTriangle size={18} />}>
                {mutationErrorIsValidation
                  ? formatValidationError(mutationError)
                  : "Something went wrong saving the case. Please try again."}
              </Alert>
            )}
          </Stack>
        </form>
      </Card>

      <CaseDangerZone caseRow={caseRow} />
    </Stack>
  );
}

// ── The intake record ────────────────────────────────────────────────
//
// What the arrival and the triage captured, read-only. The rep works these
// values in the Alpha workspace, where the capture IS the task completion
// (TMF-335), so this page shows them rather than offering a second way to
// write them. The person is the CONTACT the matter points at, and editing a
// person is /api/contacts' write, not this one.

function IntakePanel({ caseRow }: { caseRow: Case }) {
  const contact = caseRow._embedded?.contact;
  return (
    <Card>
      <Stack gap="sm">
        <h2>Intake</h2>
        <Stack gap="xs">
          <IntakeRow label="Stage" value={caseRow.stage ?? "No funnel (synced from Filevine)"} />
          <IntakeRow label="Client" value={contactName(contact) || EMPTY_VALUE} />
          <IntakeRow label="Phone" value={formatPhone(contact?.phone)} />
          <IntakeRow label="Email" value={contact?.email || EMPTY_VALUE} />
          <IntakeRow label="Source" value={caseRow.source ?? EMPTY_VALUE} />
          <IntakeRow
            label="Triage"
            value={
              caseRow.triage_tier === null
                ? "Not triaged"
                : `Tier ${caseRow.triage_tier}, score ${caseRow.triage_score ?? 0}`
            }
          />
          <IntakeRow
            label="Qualified"
            value={caseRow.qualified === null ? "Not answered" : caseRow.qualified ? "Yes" : "No"}
          />
          <IntakeRow label="Outcome" value={caseRow.disposition ?? "Still open"} />
          <IntakeRow label="Notes" value={caseRow.notes || EMPTY_VALUE} />
        </Stack>
      </Stack>
    </Card>
  );
}

function IntakeRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <strong>{label}:</strong> {value}
    </div>
  );
}

/** What a value the matter does not carry reads as. */
const EMPTY_VALUE = "\u2014";

/** The page's title: the person the matter is for, falling back to the matter's
 *  own name for one with no contact, which is the Filevine-synced case. */
function caseTitle(caseRow: Case): string {
  return contactName(caseRow._embedded?.contact) || caseRow.name || "Case";
}

// ── Danger zone ───────────────────────────────────────────────────────
//
// Deleting a case soft-deletes the row and frees the workflow it
// referenced — which can unblock a pending workflow delete (TMF-118).
// useDeleteCase already invalidates both the cases and workflows lists.

function CaseDangerZone({ caseRow }: { caseRow: Case }) {
  const navigate = useNavigate();
  const deleteMutation = useDeleteCase();

  const handleDelete = () => {
    if (!confirm(`Delete case "${caseRow.name || caseRow.id}"? This cannot be undone.`)) return;
    deleteMutation.mutate(caseRow.id, {
      onSuccess: () => {
        void navigate({ to: "/cases" });
      },
    });
  };

  const mutationError = deleteMutation.error;

  return (
    <Card accent="danger">
      <Stack gap="md">
        <h3>Danger zone</h3>
        <p>
          Delete this case. Its tasks and history are removed and it can
          no longer be acted on.
        </p>
        <div>
          <Button
            type="button"
            variant="danger"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete case"}
          </Button>
        </div>
        {mutationError !== null && (
          <Alert variant="danger" icon={<AlertTriangle size={18} />}>
            Something went wrong deleting the case. Please try again.
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

// ── Tasks panel ──────────────────────────────────────────────────────

function TasksPanel({ caseId, projectId }: { caseId: string; projectId: string }) {
  const tasksQuery = useQuery(caseTasksQueryOptions(caseId, projectId));
  // The event POST + its optimistic cache write live in useCaseTaskIssue
  // (components/CaseGateCard) so the document-upload review queue drives this
  // case's gates through the identical mutation (TMF-357).
  const { issue, pendingTaskName, pendingState } = useCaseTaskIssue(
    caseId,
    projectId,
  );

  if (tasksQuery.isPending) {
    return (
      <Card>
        <p className={css.emptyStateMuted}>Loading tasks…</p>
      </Card>
    );
  }

  if (tasksQuery.isError) {
    return (
      <Card accent="danger">
        <p>Couldn't load tasks: {(tasksQuery.error as Error).message}</p>
      </Card>
    );
  }

  const tasks = tasksQuery.data?.tasks ?? [];
  if (tasks.length === 0) {
    return (
      <Card>
        <p className={css.emptyStateMuted}>This case has no tasks yet.</p>
      </Card>
    );
  }

  return (
    <Card>
      <Stack gap="sm">
        <h2>Tasks</h2>
        <TaskTable
          tasks={tasks}
          onIssue={issue}
          pendingTaskName={pendingTaskName}
          pendingState={pendingState}
        />
      </Stack>
    </Card>
  );
}

// ── History feed ─────────────────────────────────────────────────────

const HISTORY_DEFAULT_LIMIT = 10;

function HistoryFeed({ caseId, projectId }: { caseId: string; projectId: string }) {
  const eventsQuery = useQuery(caseEventsQueryOptions(caseId, projectId));
  const [showAll, setShowAll] = useState(false);

  // Reverse-chronological for display; the verb-mapper still walks
  // ascending internally so prior-state lookup is correct.
  const annotated = useMemo(() => {
    const all = eventsQuery.data?.events ?? [];
    const desc = [...all].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    return annotateEventsWithVerbs(desc);
  }, [eventsQuery.data]);

  if (eventsQuery.isPending) {
    return (
      <Card>
        <p className={css.emptyStateMuted}>Loading history…</p>
      </Card>
    );
  }

  if (annotated.length === 0) {
    return (
      <Card>
        <Stack gap="sm">
          <h2>History</h2>
          <p className={css.emptyStateMuted}>No events recorded yet.</p>
        </Stack>
      </Card>
    );
  }

  const visible = showAll
    ? annotated
    : annotated.slice(0, HISTORY_DEFAULT_LIMIT);
  const remaining = annotated.length - visible.length;

  return (
    <Card>
      <Stack gap="sm">
        <h2>History</h2>
        <ul className={css.historyList}>
          {visible.map(({ event, verb }) => (
            <HistoryEntry key={event.id} event={event} verb={verb} />
          ))}
        </ul>
        {remaining > 0 && (
          <div className={css.historyToggle}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowAll(true)}
            >
              Show all ({annotated.length})
            </Button>
          </div>
        )}
        {showAll && annotated.length > HISTORY_DEFAULT_LIMIT && (
          <div className={css.historyToggle}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowAll(false)}
            >
              Show recent only
            </Button>
          </div>
        )}
      </Stack>
    </Card>
  );
}

function HistoryEntry({ event, verb }: { event: CaseEvent; verb: string }) {
  const taskName = taskLabelFromCondition(event.conditions[0]?.task);
  const created = new Date(event.created_at);
  const relative = relativeTime(created);
  const absolute = created.toLocaleString();
  const actorName = formatActorName(event);
  return (
    <li className={css.historyEntry}>
      <div className={css.historyHeadline}>
        <span className={css.historyActor}>{actorName}</span>
        <span>{verb}</span>
        <strong>{taskName}</strong>
      </div>
      <div className={css.historyMeta}>
        <time dateTime={event.created_at} title={absolute}>
          {relative}
        </time>
      </div>
    </li>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatActorName(event: CaseEvent): string {
  const user = event._embedded.user;
  const first = user.first_name?.trim() ?? "";
  const last = user.last_name?.trim() ?? "";
  const full = `${first} ${last}`.trim();
  if (full.length > 0) return full;
  if (user.email.length > 0) return user.email;
  return "Unknown user";
}

// Relative time formatter — simple buckets for the firm's typical
// time scales. Hover on the rendered <time> shows the absolute
// timestamp via the title attribute.
function relativeTime(when: Date): string {
  const diffMs = Date.now() - when.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  if (day < 30) return `${Math.round(day / 7)}w ago`;
  if (day < 365) return `${Math.round(day / 30)}mo ago`;
  return `${Math.round(day / 365)}y ago`;
}
